// In-page PharOS panel — trigger button + slide-in side panel + auto-attached
// page context. Purely additive; only runs on elements that opt-in via
// data-controller="pharos-page-panel". No global side effects.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "backdrop", "fab", "typing", "content", "resizeCorner", "suggestions"]

  // Toggle open/close on FAB click — Intercom-style. FAB stays visible always,
  // clicking it a second time collapses the panel.
  toggle() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.contains("is-open") ? this.close() : this.open()
  }

  open() {
    // Prevent double-open, prevent affecting the underlying page layout —
    // panel is position:fixed so it never touches the page's own DOM.
    // FAB stays visible so users can dismiss by clicking it again (Intercom-style).
    if (!this.hasPanelTarget) return
    this._restoreSize()
    this.panelTarget.classList.add("is-open")
    if (this.hasBackdropTarget) this.backdropTarget.classList.add("is-open")
    if (this.hasFabTarget) this.fabTarget.classList.add("is-panel-open")
    this._trackOpenState(true)
    this._runTypingSequence()
    this._loadSuggestions()
  }

  // Fetch starter questions the first time the panel opens. Kept out of the
  // story-page render so users who never open the panel don't pay for building
  // the page manifest. Fails silently (no suggestions shown) and allows a retry
  // on the next open if the request errors.
  _loadSuggestions() {
    if (this._suggestionsLoaded || !this.hasSuggestionsTarget) return
    const url = this.suggestionsTarget.dataset.suggestionsUrl
    if (!url) return
    this._suggestionsLoaded = true
    fetch(url, { headers: { "Accept": "text/html" }, credentials: "same-origin" })
      .then((r) => (r.ok ? r.text() : ""))
      .then((html) => { if (html) this.suggestionsTarget.innerHTML = html })
      .catch(() => { this._suggestionsLoaded = false })
  }

  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("is-open")
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove("is-open")
    if (this.hasFabTarget) this.fabTarget.classList.remove("is-panel-open")
    this._trackOpenState(false)
    // Reset typing state so next open replays the animation.
    this._typingShown = false
    if (this.hasTypingTarget) this.typingTarget.classList.remove("is-hidden")
    if (this.hasContentTarget) this.contentTarget.classList.add("is-hidden")
  }

  // Typing → real content reveal. ~750ms feels like PharOS "just came
  // online" without being long enough to annoy. Only runs once per open.
  _runTypingSequence() {
    if (this._typingShown) return
    this._typingShown = true
    if (!this.hasTypingTarget || !this.hasContentTarget) return
    // Ensure typing is visible and content is hidden at start.
    this.typingTarget.classList.remove("is-hidden")
    this.contentTarget.classList.add("is-hidden")
    setTimeout(() => {
      if (!this.panelTarget?.classList.contains("is-open")) return
      this.typingTarget.classList.add("is-hidden")
      this.contentTarget.classList.remove("is-hidden")
    }, 750)
  }

  // Wire suggestion chips: clicking one places its text into the composer's
  // textarea (existing #chat-input from _pipeline_form) so the user can edit/send.
  useSuggestion(event) {
    const text = event.currentTarget.dataset.suggestion
    if (!text) return
    const input = this.element.querySelector("#chat-input")
    if (input) {
      input.value = text
      input.focus()
      input.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  // Rewrite the standard composer textarea's placeholder to stay page-scoped
  // whenever it appears inside the panel (either the welcome-screen composer OR
  // the pipeline_form that renders after the first submit). Uses a mutation
  // observer so it also fires when Turbo streams replace the panel body with
  // the thread partial.
  _syncComposerPlaceholder() {
    const scope = this.element.querySelector(".laz-pharos-thread-composer, .laz-pharos-page-footer")
    if (!scope) return
    const ta = scope.querySelector("textarea")
    if (ta) ta.setAttribute("placeholder", "Ask about any metric, chart, or trend on this page…")
  }

  // ---- Resize (drag NW to enlarge) ----------------------------------------
  // The panel is pinned bottom-right, so growing means the top-left moves up
  // and left. We only mutate width/height; `right` and `bottom` stay fixed.
  // Size is persisted per-page in sessionStorage so users don't have to
  // re-resize every time they reopen the panel.
  //
  // Three entry points: corner (both axes), top edge (height only), left
  // edge (width only). All share _beginDrag / _duringDrag / _endDrag.
  startResizeCorner(event) { this._beginDrag(event, 'corner') }
  startResizeTop(event)    { this._beginDrag(event, 'top') }
  startResizeLeft(event)   { this._beginDrag(event, 'left') }

  _beginDrag(event, axis) {
    if (!this.hasPanelTarget) return
    event.preventDefault()
    const rect = this.panelTarget.getBoundingClientRect()
    this._dragState = {
      axis,
      startX: event.clientX,
      startY: event.clientY,
      startW: rect.width,
      startH: rect.height,
    }
    this.panelTarget.classList.add('is-resizing')
    this._boundMove = (e) => this._duringDrag(e)
    this._boundUp   = () => this._endDrag()
    window.addEventListener('mousemove', this._boundMove)
    window.addEventListener('mouseup', this._boundUp)
  }

  _duringDrag(event) {
    if (!this._dragState) return
    const s = this._dragState
    // Dragging LEFT (dx<0) grows width; UP (dy<0) grows height.
    const dx = event.clientX - s.startX
    const dy = event.clientY - s.startY
    if (s.axis === 'corner' || s.axis === 'left') {
      const newW = this._clamp(s.startW - dx, 380, window.innerWidth - 48)
      this.panelTarget.style.width = newW + 'px'
    }
    if (s.axis === 'corner' || s.axis === 'top') {
      // -116 accounts for FAB clearance (92) + top gap (24).
      const newH = this._clamp(s.startH - dy, 480, window.innerHeight - 116)
      this.panelTarget.style.height = newH + 'px'
    }
  }

  _endDrag() {
    if (!this._dragState) return
    this.panelTarget.classList.remove('is-resizing')
    window.removeEventListener('mousemove', this._boundMove)
    window.removeEventListener('mouseup', this._boundUp)
    // Persist size so next open keeps the user's preferred dimensions.
    try {
      const key = `pharos-page-panel-size:${this.element.dataset.pageId || 'default'}`
      sessionStorage.setItem(key, JSON.stringify({
        w: this.panelTarget.style.width,
        h: this.panelTarget.style.height,
      }))
    } catch (_) { /* noop */ }
    this._dragState = null
  }

  _clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)) }

  _restoreSize() {
    if (!this.hasPanelTarget) return
    try {
      const key = `pharos-page-panel-size:${this.element.dataset.pageId || 'default'}`
      const raw = sessionStorage.getItem(key)
      if (!raw) return
      const { w, h } = JSON.parse(raw)
      if (w) this.panelTarget.style.width = w
      if (h) this.panelTarget.style.height = h
    } catch (_) { /* noop */ }
  }

  // Persist open state per-page so refreshing keeps the panel in the same
  // state the user left it. Uses sessionStorage — cleared when tab closes.
  _trackOpenState(isOpen) {
    try {
      const key = `pharos-page-panel:${this.element.dataset.pageId || "default"}`
      isOpen ? sessionStorage.setItem(key, "1") : sessionStorage.removeItem(key)
    } catch (_) { /* sessionStorage can throw in private mode — safe to ignore */ }
  }

  connect() {
    // If a previous open state was tracked, restore it silently.
    try {
      const key = `pharos-page-panel:${this.element.dataset.pageId || "default"}`
      if (sessionStorage.getItem(key) === "1") {
        // defer to next tick so target queries resolve
        requestAnimationFrame(() => this.open())
      }
    } catch (_) { /* noop */ }

    // Sync composer placeholder now, and re-sync whenever the panel body is
    // replaced by a Turbo Stream (welcome → inline chat swap).
    this._syncComposerPlaceholder()
    if (typeof MutationObserver !== "undefined") {
      this._composerObserver = new MutationObserver(() => this._syncComposerPlaceholder())
      this._composerObserver.observe(this.element, { childList: true, subtree: true })
    }
  }

  disconnect() {
    if (this._composerObserver) {
      this._composerObserver.disconnect()
      this._composerObserver = null
    }
  }
}
